/* global fetch */
import { ToBackendKeys, ToFrontendKeys } from '@harvey-frontend/utils/utils'
import * as Sentry from '@sentry/react'
import _ from 'lodash'

import { REACT_APP_BACKEND_REST_URL } from '../utils/constants'
import { StreamingContainerKeys, StreamingResponseType } from './endpoints'

interface FetchBackendStreamRequest<T extends StreamingResponseType> {
  route: string
  token: string
  abortController: AbortController
  body: FormData | Record<string, unknown>
  responseKey: StreamingContainerKeys
  // If provided, the callback will be debounced with the given wait time.
  debounceWait?: number
  updateCallback?: (suggestion: T) => Promise<void>
}

export async function fetchBackendStream<T extends StreamingResponseType>({
  route,
  token,
  abortController,
  body,
  responseKey,
  updateCallback,
  debounceWait,
}: FetchBackendStreamRequest<T>): Promise<T[]> {
  const ENDPOINT = `${REACT_APP_BACKEND_REST_URL}/${route}`
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'text/event-stream',
  }

  let requestBody: string | FormData
  if (body instanceof FormData) {
    requestBody = body
  } else {
    headers['Content-Type'] = 'application/json'
    requestBody = JSON.stringify(ToBackendKeys(body))
  }

  // debounceWait defaults to undefined, which results in a function equivalent to the
  // original updateCallback.
  let debouncedCallback: _.DebouncedFunc<(resp: T) => Promise<void>> | undefined
  if (updateCallback) {
    debouncedCallback = _.debounce(updateCallback, debounceWait, {
      trailing: true,
    })
  }

  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: headers,
    body: requestBody,
    signal: abortController.signal,
  })

  if (!response.ok) {
    throw new Error(
      `A problem occurred while querying the backend (status code ${response.status})`
    )
  }

  return new Promise((resolve, reject) => {
    const reader = response.body?.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    const data: T[] = []

    function read() {
      if (!reader) {
        return
      }
      reader
        .read()
        .then(async ({ done, value }) => {
          if (done) {
            if (debouncedCallback) {
              await debouncedCallback.flush()
            }
            resolve(data)
            return
          }

          buffer += decoder.decode(value, { stream: true })
          let boundaryIndex = buffer.indexOf('\n')

          while (boundaryIndex > -1) {
            const jsonString = buffer.slice(0, boundaryIndex)
            buffer = buffer.slice(boundaryIndex + 1)

            const parsed = JSON.parse(jsonString)
            let dataToUse: T
            if (responseKey === 'object') {
              dataToUse = parsed
            } else {
              dataToUse = parsed[responseKey]
            }
            data.push(dataToUse)
            try {
              if (debouncedCallback) {
                // eslint-disable-next-line
                debouncedCallback(ToFrontendKeys(dataToUse))
              }
            } catch (error) {
              console.error(error)
              Sentry.captureException(error)
            }

            boundaryIndex = buffer.indexOf('\n')

            if (boundaryIndex > -1) {
              Sentry.captureMessage(
                `Processing multiple JSON objects in a single stream response from ${ENDPOINT}`,
                'info'
              )
            }
          }

          if (buffer.length > 0) {
            Sentry.captureMessage(
              `Buffer not empty at end of stream chunk from ${ENDPOINT}`,
              'info'
            )
          }

          read()
        })
        .catch((error) => {
          Sentry.captureException(error)
          reject(error)
        })
    }

    read()
  })
}

// https://stackoverflow.com/questions/39617221/lengthy-http-calls-failing-in-taskpane-apps-on-office-for-mac-client/40224845
// https://stackoverflow.com/questions/43789247/excel-for-mac-js-add-in-http-request-timeout
export async function fetchBackendXhr(
  route: string,
  body: FormData | Record<string, unknown>,
  token: string
): Promise<unknown> {
  const ENDPOINT = REACT_APP_BACKEND_REST_URL + '/' + route
  const promise = new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.withCredentials = true
    xhr.timeout = 600000
    xhr.open('POST', ENDPOINT, true)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText))
        } else {
          reject(xhr.statusText)
        }
      }
    }
    xhr.send(JSON.stringify(body))
  })
  return promise
}
